import { DefaultNoRowsOverlay } from './Components/NoRowsOverlay';
import { CellConfiguration } from './Components/Cells/CellLoader';
import React, { useCallback, useMemo, useState } from 'react';
import {
  DataGridProProps,
  GridColumnHeaders,
  GridPaginationModel,
  GridRowId,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { Pagination } from './Components/Pagination/Pagination';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { styled } from '@mui/material/styles';
import { usePrepareConfiguration } from './hooks/usePrepareConfiguration';
import { useDataGridDialogs } from './hooks/useDataGridDialogs';
import { Action, ColumnConfiguration } from './models/configurableTable';
import { BaseGrid } from './Components/Base/BaseGrid';
import { SortingParam } from '../Filters/constants/sorting';
import { DefaultLoadingOverlay } from './Components/Loaders/DefaultLoadingOverlay';
import { DefaultColumnHeaderSortIcon } from './Components/Header/DefaultColumnSortIcon';

export type ConfigurableTableProps = {
  sort?: SortingParam;
  disablePagination?: boolean;
  disableColumnReorder?: boolean;
  disableVirtualization?: DataGridProProps['disableVirtualization'];
  disableRowSelectionExcludeModel?: DataGridProProps['disableRowSelectionExcludeModel'];
  configuration: ColumnConfiguration[];
  cellConfiguration?: CellConfiguration;
  cellProps?: any;
  apiRef?: DataGridProProps['apiRef'];
  //TODO: Imporve typings (generic)
  rows?: Record<string, any>[];
  totalNumberOfItems?: number;
  actions?: Action[];
  'data-testid'?: string;
  page?: number;
  pageSize?: number;
  onPaginationChange?: (pagination: GridPaginationModel) => void;
  slots?: DataGridProProps['slots'];
  slotProps?: DataGridProProps['slotProps'];
  getDetailPanelContent?: DataGridProProps['getDetailPanelContent'];
  getDetailPanelHeight?: DataGridProProps['getDetailPanelHeight'];
  checkboxSelection?: DataGridProProps['checkboxSelection'];
  rowSelectionModel?: DataGridProProps['rowSelectionModel'];
  loading?: DataGridProProps['loading'];
  getRowId?: DataGridProProps['getRowId'];
  getRowHeight?: DataGridProProps['getRowHeight'];
  onSortModelChange?: DataGridProProps['onSortModelChange'];
  onRowSelectionModelChange?: DataGridProProps['onRowSelectionModelChange'];
  initialState?: DataGridProProps['initialState'];
  disableRowSelectionOnClick?: boolean;
  onRowSelect?: (itemId: string) => void;
  fillerWidth?: number;
};

const CustumizedKeyboardArrowDownIcon = styled(KeyboardArrowDownIcon)({ fontSize: '1.5rem' });
const CustumizedKeyboardArrowUpIcon = styled(KeyboardArrowUpIcon)({ fontSize: '1.5rem' });

export const ConfigurableTable = ({
  sort,
  disablePagination,
  configuration,
  cellConfiguration,
  cellProps,
  rows = [],
  totalNumberOfItems,
  actions,
  page = 0,
  pageSize = 25,
  onPaginationChange,
  slots,
  disableRowSelectionOnClick,
  onRowSelect,
  'data-testid': testid,
  //HACK for details table wider then main table (maybe in feature improve that with automatic calculation )
  fillerWidth,
  ...props
}: ConfigurableTableProps) => {
  const [expanded, setExpaned] = useState(false);
  const { dialogs, selectDialog, unselectDialog, dialogActions } = useDataGridDialogs({
    actions,
  });

  const columnsConfiguration = usePrepareConfiguration({
    detailPanelEnabled: props.getDetailPanelContent !== undefined,
    configuration,
    cellConfiguration,
    cellProps,
    actions,
    selectDialog,
    fillerWidth: expanded ? fillerWidth : undefined,
  });

  const paginationModel = useMemo(() => {
    return {
      page: page,
      pageSize: pageSize,
    };
  }, [page, pageSize]);

  const sortModel = useMemo(() => {
    return sort
      ? [
          {
            field: sort.columnName,
            sort: sort.sort,
          },
        ]
      : [];
  }, [sort]);

  const handleRowClick = useCallback(
    (row: GridRowParams) => {
      onRowSelect?.(row.id.toString());
    },
    [onRowSelect],
  );

  const onDetailPanelExpandedRowIdsChange = useCallback((rows: Set<GridRowId>) => {
    setExpaned(rows.size > 0);
  }, []);

  return (
    <>
      <BaseGrid
        onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
        {...props}
        disableColumnSorting
        disableColumnReorder
        disablePagination={disablePagination}
        columns={columnsConfiguration}
        rowCount={totalNumberOfItems}
        onPaginationModelChange={onPaginationChange}
        paginationModel={disablePagination ? undefined : paginationModel}
        rows={rows}
        sortModel={sortModel}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        onRowClick={handleRowClick}
        slotProps={{
          root: {
            'data-testid': testid,
          },
        }}
        slots={{
          columnHeaderSortIcon: DefaultColumnHeaderSortIcon,
          noRowsOverlay: DefaultNoRowsOverlay,
          pagination: Pagination,
          loadingOverlay: DefaultLoadingOverlay,
          columnHeaders: (props) => <GridColumnHeaders {...props} data-testid="headers" />,
          // @ts-expect-error: MUI Docs say the types for their own icons aren't compatible with the DataGrid icons..
          detailPanelExpandIcon: CustumizedKeyboardArrowDownIcon,
          // @ts-expect-error: MUI Docs say the types for their own icons aren't compatible with the DataGrid icons..
          detailPanelCollapseIcon: CustumizedKeyboardArrowUpIcon,
          ...slots,
        }}
      />

      {dialogActions.map((action: any) => {
        return (
          <React.Fragment key={action.id}>
            <action.Component
              open={!!dialogs[action.id]}
              data={dialogs[action.id]}
              onClose={() => {
                unselectDialog(action.id);
              }}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};
